/**
 * Table, toolbar-free. DESIGN.md §Tables: a surface wrap with a 1px divider, 20px radius
 * and horizontal scroll; 14px cells at 12px 16px over 1px dividers; a 12px/700 muted
 * header row on the sunken fill; rows tint sunken on hover; columns marked hideOnPhone
 * hide under 820px; the pager is a foot row (muted 14px summary left, small buttons
 * right) over a divider. Reference: stars.css `.table-wrap`, `.table`, `.pager`.
 */
import React from "react";
import { Button } from "./Button";
import StarsIcon from "../../styles/StarsIcon";
import { cn } from "./cn";

export function TableWrap({ className, ...props }) {
  return (
    <div
      className={cn(
        "tw-overflow-x-auto tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface",
        className,
      )}
      {...props}
    />
  );
}

export function Table({ className, ...props }) {
  return (
    <table
      className={cn(
        "tw-w-full tw-border-collapse tw-text-sm tw-text-text",
        className,
      )}
      {...props}
    />
  );
}

export function THead(props) {
  return <thead {...props} />;
}

export function TBody(props) {
  return <tbody {...props} />;
}

export function Tr({ className, ...props }) {
  return (
    <tr className={cn("hover:tw-bg-surface-sunken", className)} {...props} />
  );
}

const cell = (hideOnPhone, align) =>
  cn(
    "tw-px-4 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-align-middle",
    align === "right" ? "tw-text-right tw-whitespace-nowrap" : "tw-text-left",
    hideOnPhone && "max-rail:tw-hidden",
  );

export function Th({
  hideOnPhone = false,
  align = "left",
  className,
  ...props
}) {
  return (
    <th
      scope="col"
      data-hide-on-phone={hideOnPhone ? "true" : undefined}
      data-align={align}
      className={cn(
        cell(hideOnPhone, align),
        "tw-whitespace-nowrap tw-bg-surface-sunken tw-text-xs tw-font-bold tw-tracking-wide tw-text-text-muted",
        className,
      )}
      {...props}
    />
  );
}

export function Td({
  hideOnPhone = false,
  align = "left",
  className,
  ...props
}) {
  return (
    <td
      data-hide-on-phone={hideOnPhone ? "true" : undefined}
      data-align={align}
      className={cn(cell(hideOnPhone, align), className)}
      {...props}
    />
  );
}

export function TablePager({ page, pageCount, summary, onPage, className }) {
  return (
    <div
      className={cn(
        "tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-px-4 tw-py-3 tw-text-sm tw-text-text-muted",
        className,
      )}
    >
      <span>{summary}</span>
      <div className="tw-flex tw-gap-2">
        <Button
          variant="icon"
          size="sm"
          iconOnly
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <StarsIcon name="chevronLeft" className="tw-h-4 tw-w-4" />
        </Button>
        <Button
          variant="icon"
          size="sm"
          iconOnly
          aria-label="Next page"
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
        >
          <StarsIcon name="chevron" className="tw-h-4 tw-w-4" />
        </Button>
      </div>
    </div>
  );
}
